import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { IPaddock } from 'interfaces/IPaddock';
import type { IZone } from 'interfaces/IZone';
import PaddockService from 'services/PaddockService';

import {
  isFarm,
  isZone,
  readCachedFarm,
  writeFarmToCache,
  writeZonesToCache,
} from './farmsCache';

interface UpsertPaddockArgs {
  farmId: string;
  paddock: IPaddock;
  /** The paddock's zone when it's created or changes; null otherwise. */
  zone: IZone | null;
}

/**
 * Creates, updates, excludes or re-includes a paddock through Paddock/Upsert
 * (OP-2481). A new paddock's default zone is saved in the same call, and the
 * zones list is refreshed so the map picks it up.
 */
export const useUpsertPaddock = (orgId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ farmId, paddock, zone }: UpsertPaddockArgs) =>
      PaddockService.Upsert(farmId, paddock, zone),
    onSuccess: (response, { farmId, paddock, zone }) => {
      if (!orgId) return;

      const data = response?.data ?? [];
      const savedFarm = data.find(isFarm);
      const savedZones = data.filter(isZone);

      if (savedFarm) {
        writeFarmToCache(queryClient, orgId, savedFarm);
      } else {
        // No farm in the response — patch the paddock into the cached farm.
        const cachedFarm = readCachedFarm(queryClient, orgId, farmId);
        if (cachedFarm) {
          const exists = cachedFarm.paddocks.some(
            (item) => item.paddockId === paddock.paddockId
          );
          writeFarmToCache(queryClient, orgId, {
            ...cachedFarm,
            paddocks: exists
              ? cachedFarm.paddocks.map((item) =>
                  item.paddockId === paddock.paddockId ? paddock : item
                )
              : [...cachedFarm.paddocks, paddock],
          });
        }
      }

      if (savedZones.length > 0) {
        writeZonesToCache(queryClient, orgId, savedZones);
      } else if (zone) {
        // No zones in the response — patch the zone we sent into the cache.
        writeZonesToCache(queryClient, orgId, [zone]);
      }
    },
  });
};

export default useUpsertPaddock;
